"use client";

import { useEffect, useState } from "react";
import { getCropGeometry } from "@/lib/image-crop";
import { getFaceWindow } from "@/lib/print";
import type { LocalCartItem } from "@/lib/local-cart";

type ProductArtworkThumbnailProps = {
  item: Pick<LocalCartItem, "product" | "artwork">;
  className?: string;
};

export default function ProductArtworkThumbnail({
  item,
  className = "",
}: ProductArtworkThumbnailProps) {
  const area = item.product.imageAreas[0];
  const crop = area ? item.artwork[area.id] : undefined;
  const file = crop?.file;
  const [imagePreview, setImagePreview] = useState<{
    file: File;
    url: string;
  } | null>(null);
  const [sourceSize, setSourceSize] = useState<{
    file: File;
    width: number;
    height: number;
  } | null>(null);

  useEffect(() => {
    if (file) {
      let active = true;
      const url = URL.createObjectURL(file);
      queueMicrotask(() => {
        if (active) {
          setImagePreview({ file, url });
        }
      });
      return () => {
        active = false;
        URL.revokeObjectURL(url);
      };
    }
  }, [file]);

  const currentPreview =
    imagePreview?.file === file ? imagePreview : null;
  const currentSourceSize =
    sourceSize?.file === file ? sourceSize : null;
  const faceWindow =
    area && crop && currentSourceSize
      ? getFaceWindow(
          area,
          getCropGeometry(
            area,
            crop,
            currentSourceSize.width,
            currentSourceSize.height,
          ).window,
        )
      : null;
  const imageStyle = faceWindow
    ? {
        left: `${(-faceWindow.left / faceWindow.width) * 100}%`,
        top: `${(-faceWindow.top / faceWindow.height) * 100}%`,
        width: `${100 / faceWindow.width}%`,
        height: `${100 / faceWindow.height}%`,
      }
    : undefined;

  return (
    <div
      className={`product-artwork-thumbnail ${className}`}
      aria-hidden="true"
    >
      <div
        className={`product-artwork-thumbnail-frame product-artwork-thumbnail-${area?.shape ?? "rectangle"}`}
        style={
          area
            ? { aspectRatio: `${area.widthCm} / ${area.heightCm}` }
            : undefined
        }
      >
        {currentPreview && (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={currentPreview.url}
            alt=""
            onLoad={(event) => {
              if (!file) {
                return;
              }
              setSourceSize({
                file,
                width: event.currentTarget.naturalWidth,
                height: event.currentTarget.naturalHeight,
              });
            }}
            style={imageStyle}
          />
        )}
        {!currentPreview && (
          <span>{item.product.name.slice(0, 1)}</span>
        )}
      </div>
    </div>
  );
}
